import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Avatar
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  FilterList as FilterIcon,
  TrendingUp,
  TrendingDown,
  AccountBalance,
  Inventory
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

interface Transaction {
  id: string;
  data: string;
  tip: 'venit' | 'cheltuiala';
  categorie: string;
  descriere: string;
  suma: number;
  parohieId?: string;
  status: 'confirmat' | 'in_asteptare' | 'anulat';
}

interface FacturaIntrare {
  id: string;
  numar: string;
  data: string;
  furnizor: string;
  descriere: string;
  suma: number;
  tva: number;
  total: number;
  parohieId?: string;
  status: 'neplatita' | 'platita' | 'anulata';
  dataScadenta: string;
  observatii?: string;
}

interface FacturaIesire {
  id: string;
  numar: string;
  data: string;
  client: string;
  descriere: string;
  suma: number;
  tva: number;
  total: number;
  parohieId?: string;
  status: 'emisa' | 'platita' | 'anulata';
  dataScadenta: string;
  observatii?: string;
}

interface DevizaMateriale {
  id: string;
  numar: string;
  data: string;
  furnizor: string;
  materiale: Array<{
    nume: string;
    cantitate: number;
    unitate: string;
    pretUnitar: number;
    total: number;
  }>;
  total: number;
  parohieId?: string;
  status: 'in_asteptare' | 'aprobata' | 'anulata';
  observatii?: string;
}

interface BonTransfer {
  id: string;
  numar: string;
  data: string;
  deLa: string;
  la: string;
  suma: number;
  motiv: string;
  parohieId?: string;
  status: 'in_asteptare' | 'efectuat' | 'anulat';
  observatii?: string;
}

interface Plata {
  id: string;
  data: string;
  tip: 'factura_intrare' | 'factura_iesire' | 'bon_transfer' | 'alte_plati';
  documentId: string;
  documentNumar: string;
  suma: number;
  metodaPlata: 'cash' | 'transfer' | 'card' | 'cec';
  parohieId?: string;
  status: 'efectuata' | 'in_asteptare' | 'anulata';
  observatii?: string;
}

interface GestiuneItem {
  id: string;
  nume: string;
  parohieId?: string;
  parohieNume?: string;
  tip: 'magazie' | 'depozit' | 'sacristie' | 'biblioteca' | 'alt';
  locatie: string;
  responsabil: string;
  responsabilId?: string;
  status: 'activ' | 'inactiv' | 'renovare';
  descriere?: string;
  createdAt: string;
  updatedAt: string;
}

interface ProdusGestiune {
  id: string;
  nume: string;
  categorie: string;
  cantitate: number;
  unitate: string;
  pretUnitar: number;
  valoareTotala: number;
  gestiuneId: string;
  gestiuneNume?: string;
  parohieId?: string;
  parohieNume?: string;
  status: 'in_stoc' | 'consumat' | 'transferat' | 'vandut' | 'deteriorat';
  dataIntrare: string;
  dataIesire?: string;
  documentIntrare?: string;
  documentIesire?: string;
  observatii?: string;
}

interface MiscareProdus {
  id: string;
  data: string;
  tip: 'intrare' | 'iesire' | 'transfer' | 'ajustare';
  produsId: string;
  produsNume: string;
  cantitate: number;
  gestiuneId: string;
  gestiuneNume?: string;
  documentId?: string;
  documentNumar?: string;
  motiv?: string;
  utilizator: string;
  observatii?: string;
}

interface InventoryItem {
  id: string;
  nume: string;
  categorie: string;
  cantitate: number;
  unitate: string;
  valoare: number;
  parohieId?: string;
}

const Gestiune: React.FC = () => {
  const { hasPermission } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [subTab, setSubTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTip, setFilterTip] = useState('');
  const [filterCategorie, setFilterCategorie] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);

  // Mock data pentru tranzacții
  const [transactions] = useState<Transaction[]>([
    {
      id: '1',
      data: '2024-01-15',
      tip: 'venit',
      categorie: 'Donatii',
      descriere: 'Donatie de la credincioși',
      suma: 2500,
      parohieId: 'parohia1',
      status: 'confirmat'
    },
    {
      id: '2',
      data: '2024-01-16',
      tip: 'cheltuiala',
      categorie: 'Utilitati',
      descriere: 'Factura electricitate',
      suma: 450,
      parohieId: 'parohia1',
      status: 'confirmat'
    },
    {
      id: '3',
      data: '2024-01-17',
      tip: 'venit',
      categorie: 'Subventii',
      descriere: 'Subventie pentru renovare',
      suma: 10000,
      parohieId: 'parohia2',
      status: 'in_asteptare'
    },
    {
      id: '4',
      data: '2024-01-18',
      tip: 'cheltuiala',
      categorie: 'Renovari',
      descriere: 'Materiale pentru renovare',
      suma: 3500,
      parohieId: 'parohia2',
      status: 'confirmat'
    }
  ]);

  // Mock data pentru facturi intrare
  const [facturiIntrare] = useState<FacturaIntrare[]>([
    {
      id: 'fi1',
      numar: 'FI-001/2024',
      data: '2024-01-15',
      furnizor: 'SC Materiale Constructii SRL',
      descriere: 'Materiale pentru renovare',
      suma: 5000,
      tva: 950,
      total: 5950,
      parohieId: 'parohia1',
      status: 'neplatita',
      dataScadenta: '2024-02-15',
      observatii: 'Factură urgentă'
    },
    {
      id: 'fi2',
      numar: 'FI-002/2024',
      data: '2024-01-20',
      furnizor: 'SC Utilitati Publice SA',
      descriere: 'Factură electricitate',
      suma: 800,
      tva: 152,
      total: 952,
      parohieId: 'parohia1',
      status: 'platita',
      dataScadenta: '2024-02-20'
    },
    {
      id: 'fi3',
      numar: 'FI-003/2024',
      data: '2024-01-25',
      furnizor: 'SC Libraria Religioasa SRL',
      descriere: 'Cărți religioase și spirituale',
      suma: 1200,
      tva: 228,
      total: 1428,
      parohieId: 'parohia1',
      status: 'neplatita',
      dataScadenta: '2024-02-25',
      observatii: 'Pentru biblioteca parohiei'
    },
    {
      id: 'fi4',
      numar: 'FI-004/2024',
      data: '2024-01-28',
      furnizor: 'SC Atelier Icoane SRL',
      descriere: 'Icoane religioase și obiecte de cult',
      suma: 1800,
      tva: 342,
      total: 2142,
      parohieId: 'parohia1',
      status: 'neplatita',
      dataScadenta: '2024-02-28',
      observatii: 'Icoane pentru altar și iconostas'
    }
  ]);

  // Mock data pentru facturi ieșire
  const [facturiIesire] = useState<FacturaIesire[]>([
    {
      id: 'fe1',
      numar: 'FE-001/2024',
      data: '2024-01-10',
      client: 'Credinciosul Ion Popescu',
      descriere: 'Servicii religioase - botez',
      suma: 200,
      tva: 38,
      total: 238,
      parohieId: 'parohia1',
      status: 'emisa',
      dataScadenta: '2024-02-10'
    }
  ]);

  // Mock data pentru devize materiale
  const [devizeMateriale] = useState<DevizaMateriale[]>([
    {
      id: 'dm1',
      numar: 'DM-001/2024',
      data: '2024-01-18',
      furnizor: 'SC Materiale Bisericesti SRL',
      materiale: [
        { nume: 'Candelabru de argint', cantitate: 2, unitate: 'buc', pretUnitar: 500, total: 1000 },
        { nume: 'Cărți religioase', cantitate: 50, unitate: 'buc', pretUnitar: 25, total: 1250 },
        { nume: 'Mobilier bisericesc', cantitate: 1, unitate: 'set', pretUnitar: 2000, total: 2000 }
      ],
      total: 4250,
      parohieId: 'parohia1',
      status: 'in_asteptare',
      observatii: 'Pentru renovarea bisericii'
    }
  ]);

  // Mock data pentru bonuri transfer
  const [bonuriTransfer] = useState<BonTransfer[]>([
    {
      id: 'bt1',
      numar: 'BT-001/2024',
      data: '2024-01-22',
      deLa: 'Parohia Sf. Nicolae',
      la: 'Episcopia',
      suma: 10000,
      motiv: 'Contribuție anuală',
      parohieId: 'parohia1',
      status: 'efectuat',
      observatii: 'Transfer efectuat prin bancă'
    }
  ]);

  // Mock data pentru plăți
  const [plati] = useState<Plata[]>([
    {
      id: 'p1',
      data: '2024-01-25',
      tip: 'factura_intrare',
      documentId: 'fi2',
      documentNumar: 'FI-002/2024',
      suma: 952,
      metodaPlata: 'transfer',
      parohieId: 'parohia1',
      status: 'efectuata',
      observatii: 'Plată efectuată prin transfer bancar'
    }
  ]);

  // Mock data pentru gestiuni
  const [gestiuni] = useState<GestiuneItem[]>([
    {
      id: 'gest1',
      nume: 'Magazia Principală',
      parohieId: 'parohia1',
      parohieNume: 'Parohia Sf. Nicolae',
      tip: 'magazie',
      locatie: 'Etaj 1, Camera 101',
      responsabil: 'Părintele Gheorghe',
      responsabilId: 'user3',
      status: 'activ',
      descriere: 'Magazia principală pentru materiale și obiecte de cult',
      createdAt: '2023-01-01',
      updatedAt: '2024-01-15'
    },
    {
      id: 'gest2',
      nume: 'Sacristia',
      parohieId: 'parohia1',
      parohieNume: 'Parohia Sf. Nicolae',
      tip: 'sacristie',
      locatie: 'Lângă altar',
      responsabil: 'Părintele Mihai',
      responsabilId: 'user6',
      status: 'activ',
      descriere: 'Sacristia pentru obiecte de cult și veșminte',
      createdAt: '2023-01-01',
      updatedAt: '2024-01-10'
    },
    {
      id: 'gest3',
      nume: 'Depozitul Central',
      parohieId: 'parohia2',
      parohieNume: 'Parohia Sf. Gheorghe',
      tip: 'depozit',
      locatie: 'Subsol, Camera 001',
      responsabil: 'Părintele Vasile',
      responsabilId: 'user4',
      status: 'activ',
      descriere: 'Depozit central pentru materiale de construcție',
      createdAt: '2023-02-01',
      updatedAt: '2024-01-05'
    }
  ]);

  // Mock data pentru produse în gestiuni
  const [produseGestiuni] = useState<ProdusGestiune[]>([
    {
      id: 'prod1',
      nume: 'Candelabru de argint',
      categorie: 'Obiecte de cult',
      cantitate: 2,
      unitate: 'buc',
      pretUnitar: 500,
      valoareTotala: 1000,
      gestiuneId: 'gest2',
      gestiuneNume: 'Sacristia',
      parohieId: 'parohia1',
      parohieNume: 'Parohia Sf. Nicolae',
      status: 'in_stoc',
      dataIntrare: '2024-01-15',
      documentIntrare: 'DM-001/2024',
      observatii: 'Candelabre pentru altar'
    },
    {
      id: 'prod2',
      nume: 'Cărți religioase',
      categorie: 'Biblioteca',
      cantitate: 50,
      unitate: 'buc',
      pretUnitar: 25,
      valoareTotala: 1250,
      gestiuneId: 'gest1',
      gestiuneNume: 'Magazia Principală',
      parohieId: 'parohia1',
      parohieNume: 'Parohia Sf. Nicolae',
      status: 'in_stoc',
      dataIntrare: '2024-01-18',
      documentIntrare: 'DM-001/2024',
      observatii: 'Cărți pentru biblioteca parohiei'
    },
    {
      id: 'prod3',
      nume: 'Mobilier bisericesc',
      categorie: 'Mobilier',
      cantitate: 1,
      unitate: 'set',
      pretUnitar: 2000,
      valoareTotala: 2000,
      gestiuneId: 'gest1',
      gestiuneNume: 'Magazia Principală',
      parohieId: 'parohia1',
      parohieNume: 'Parohia Sf. Nicolae',
      status: 'in_stoc',
      dataIntrare: '2024-01-18',
      documentIntrare: 'DM-001/2024',
      observatii: 'Set complet mobilier pentru biserică'
    },
    {
      id: 'prod4',
      nume: 'Materiale construcție',
      categorie: 'Materiale',
      cantitate: 100,
      unitate: 'kg',
      pretUnitar: 5,
      valoareTotala: 500,
      gestiuneId: 'gest3',
      gestiuneNume: 'Depozitul Central',
      parohieId: 'parohia2',
      parohieNume: 'Parohia Sf. Gheorghe',
      status: 'consumat',
      dataIntrare: '2024-01-10',
      dataIesire: '2024-01-20',
      documentIntrare: 'FI-001/2024',
      documentIesire: 'Consum renovare',
      observatii: 'Ciment pentru renovare'
    }
  ]);

  // Mock data pentru mișcări produse
  const [miscariProduse] = useState<MiscareProdus[]>([
    {
      id: 'misc1',
      data: '2024-01-15',
      tip: 'intrare',
      produsId: 'prod1',
      produsNume: 'Candelabru de argint',
      cantitate: 2,
      gestiuneId: 'gest2',
      gestiuneNume: 'Sacristia',
      documentId: 'dm1',
      documentNumar: 'DM-001/2024',
      utilizator: 'Părintele Gheorghe',
      observatii: 'Intrare din deviza materiale'
    },
    {
      id: 'misc2',
      data: '2024-01-20',
      tip: 'iesire',
      produsId: 'prod4',
      produsNume: 'Materiale construcție',
      cantitate: 100,
      gestiuneId: 'gest3',
      gestiuneNume: 'Depozitul Central',
      motiv: 'Consum pentru renovare',
      utilizator: 'Părintele Vasile',
      observatii: 'Consumat în procesul de renovare'
    }
  ]);

  // Mock data pentru inventar
  const [inventory] = useState<InventoryItem[]>([
    {
      id: '1',
      nume: 'Candelabru de argint',
      categorie: 'Obiecte de cult',
      cantitate: 2,
      unitate: 'buc',
      valoare: 1500,
      parohieId: 'parohia1'
    },
    {
      id: '2',
      nume: 'Cărți religioase',
      categorie: 'Biblioteca',
      cantitate: 150,
      unitate: 'buc',
      valoare: 3000,
      parohieId: 'parohia1'
    },
    {
      id: '3',
      nume: 'Mobilier bisericesc',
      categorie: 'Mobilier',
      cantitate: 10,
      unitate: 'buc',
      valoare: 5000,
      parohieId: 'parohia2'
    }
  ]);

  const transactionCategories = ['Donatii', 'Subventii', 'Utilitati', 'Renovari', 'Salarii', 'Altele'];
  const inventoryCategories = ['Obiecte de cult', 'Biblioteca', 'Mobilier', 'Tehnică', 'Altele'];

  const filteredTransactions = transactions.filter(transaction => {
    const matchesSearch = transaction.descriere.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         transaction.categorie.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = !filterTip || transaction.tip === filterTip;
    const matchesCategory = !filterCategorie || transaction.categorie === filterCategorie;
    
    return matchesSearch && matchesType && matchesCategory;
  });

  const filteredInventory = inventory.filter(item => {
    const matchesSearch = item.nume.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.categorie.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !filterCategorie || item.categorie === filterCategorie;
    
    return matchesSearch && matchesCategory;
  });

  const totalVenituri = transactions
    .filter(t => t.tip === 'venit' && t.status === 'confirmat')
    .reduce((sum, t) => sum + t.suma, 0);

  const totalCheltuieli = transactions
    .filter(t => t.tip === 'cheltuiala' && t.status === 'confirmat')
    .reduce((sum, t) => sum + t.suma, 0);

  const sold = totalVenituri - totalCheltuieli;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmat': return 'success';
      case 'in_asteptare': return 'warning';
      case 'anulat': return 'error';
      default: return 'default';
    }
  };

  const handleViewTransaction = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedTransaction(null);
  };

  const TabPanel = ({ children, value, index }: { children: React.ReactNode; value: number; index: number }) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Gestiune
        </Typography>
        {hasPermission('gestiune.write') && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {/* Implementare adăugare tranzacție nouă */}}
          >
            Tranzacție nouă
          </Button>
        )}
      </Box>

      {/* Statistici financiare */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingUp color="success" sx={{ mr: 1 }} />
                <Typography color="textSecondary">
                  Venituri totale
                </Typography>
              </Box>
              <Typography variant="h5" color="success.main">
                €{totalVenituri.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingDown color="error" sx={{ mr: 1 }} />
                <Typography color="textSecondary">
                  Cheltuieli totale
                </Typography>
              </Box>
              <Typography variant="h5" color="error.main">
                €{totalCheltuieli.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <AccountBalance color="primary" sx={{ mr: 1 }} />
                <Typography color="textSecondary">
                  Sold
                </Typography>
              </Box>
              <Typography variant="h5" color={sold >= 0 ? 'success.main' : 'error.main'}>
                €{sold.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Inventory color="info" sx={{ mr: 1 }} />
                <Typography color="textSecondary">
                  Valoare inventar
                </Typography>
              </Box>
              <Typography variant="h5" color="info.main">
                €{inventory.reduce((sum, item) => sum + item.valoare, 0).toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ width: '100%' }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Tranzacții" />
          <Tab label="Facturi Intrare" />
          <Tab label="Facturi Ieșire" />
          <Tab label="Devize Materiale" />
          <Tab label="Bonuri Transfer" />
          <Tab label="Plăți" />
          <Tab label="Gestiuni" />
          <Tab label="Inventar" />
        </Tabs>

        {/* Tab Tranzacții */}
        <TabPanel value={activeTab} index={0}>
          {/* Filtre pentru tranzacții */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="Caută tranzacții..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Tip</InputLabel>
                  <Select
                    value={filterTip}
                    onChange={(e) => setFilterTip(e.target.value)}
                    label="Tip"
                  >
                    <MenuItem value="">Toate</MenuItem>
                    <MenuItem value="venit">Venit</MenuItem>
                    <MenuItem value="cheltuiala">Cheltuială</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Categorie</InputLabel>
                  <Select
                    value={filterCategorie}
                    onChange={(e) => setFilterCategorie(e.target.value)}
                    label="Categorie"
                  >
                    <MenuItem value="">Toate</MenuItem>
                    {transactionCategories.map(category => (
                      <MenuItem key={category} value={category}>{category}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<FilterIcon />}
                  onClick={() => {
                    setSearchTerm('');
                    setFilterTip('');
                    setFilterCategorie('');
                  }}
                >
                  Resetează
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Tabel tranzacții */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell>Tip</TableCell>
                  <TableCell>Categorie</TableCell>
                  <TableCell>Descriere</TableCell>
                  <TableCell>Suma</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Acțiuni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTransactions.map((transaction) => (
                  <TableRow key={transaction.id}>
                    <TableCell>{transaction.data}</TableCell>
                    <TableCell>
                      <Chip
                        label={transaction.tip}
                        color={transaction.tip === 'venit' ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{transaction.categorie}</TableCell>
                    <TableCell>{transaction.descriere}</TableCell>
                    <TableCell>
                      <Typography
                        color={transaction.tip === 'venit' ? 'success.main' : 'error.main'}
                        fontWeight="bold"
                      >
                        {transaction.tip === 'venit' ? '+' : '-'}€{transaction.suma.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={transaction.status}
                        color={getStatusColor(transaction.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => handleViewTransaction(transaction)}
                        color="primary"
                      >
                        <ViewIcon />
                      </IconButton>
                      {hasPermission('gestiune.write') && (
                        <IconButton color="secondary">
                          <EditIcon />
                        </IconButton>
                      )}
                      <IconButton color="default">
                        <DownloadIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Tab Facturi Intrare */}
        <TabPanel value={activeTab} index={1}>
          <Typography variant="h6" gutterBottom>
            Facturi Intrare
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Număr</TableCell>
                  <TableCell>Data</TableCell>
                  <TableCell>Furnizor</TableCell>
                  <TableCell>Descriere</TableCell>
                  <TableCell>Suma</TableCell>
                  <TableCell>TVA</TableCell>
                  <TableCell>Total</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Data scadență</TableCell>
                  <TableCell>Acțiuni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {facturiIntrare.map((factura) => (
                  <TableRow key={factura.id}>
                    <TableCell>{factura.numar}</TableCell>
                    <TableCell>{factura.data}</TableCell>
                    <TableCell>{factura.furnizor}</TableCell>
                    <TableCell>{factura.descriere}</TableCell>
                    <TableCell>€{factura.suma.toLocaleString()}</TableCell>
                    <TableCell>€{factura.tva.toLocaleString()}</TableCell>
                    <TableCell>
                      <Typography fontWeight="bold">
                        €{factura.total.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={factura.status}
                        color={factura.status === 'platita' ? 'success' : factura.status === 'neplatita' ? 'warning' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{factura.dataScadenta}</TableCell>
                    <TableCell>
                      <IconButton color="primary">
                        <ViewIcon />
                      </IconButton>
                      <IconButton color="secondary">
                        <EditIcon />
                      </IconButton>
                      <IconButton color="default">
                        <DownloadIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Tab Facturi Ieșire */}
        <TabPanel value={activeTab} index={2}>
          <Typography variant="h6" gutterBottom>
            Facturi Ieșire
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Număr</TableCell>
                  <TableCell>Data</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Descriere</TableCell>
                  <TableCell>Suma</TableCell>
                  <TableCell>TVA</TableCell>
                  <TableCell>Total</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Data scadență</TableCell>
                  <TableCell>Acțiuni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {facturiIesire.map((factura) => (
                  <TableRow key={factura.id}>
                    <TableCell>{factura.numar}</TableCell>
                    <TableCell>{factura.data}</TableCell>
                    <TableCell>{factura.client}</TableCell>
                    <TableCell>{factura.descriere}</TableCell>
                    <TableCell>€{factura.suma.toLocaleString()}</TableCell>
                    <TableCell>€{factura.tva.toLocaleString()}</TableCell>
                    <TableCell>
                      <Typography fontWeight="bold">
                        €{factura.total.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={factura.status}
                        color={factura.status === 'platita' ? 'success' : factura.status === 'emisa' ? 'info' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{factura.dataScadenta}</TableCell>
                    <TableCell>
                      <IconButton color="primary">
                        <ViewIcon />
                      </IconButton>
                      <IconButton color="secondary">
                        <EditIcon />
                      </IconButton>
                      <IconButton color="default">
                        <DownloadIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Tab Devize Materiale */}
        <TabPanel value={activeTab} index={3}>
          <Typography variant="h6" gutterBottom>
            Devize Materiale
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Număr</TableCell>
                  <TableCell>Data</TableCell>
                  <TableCell>Furnizor</TableCell>
                  <TableCell>Materiale</TableCell>
                  <TableCell>Total</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Acțiuni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {devizeMateriale.map((deviza) => (
                  <TableRow key={deviza.id}>
                    <TableCell>{deviza.numar}</TableCell>
                    <TableCell>{deviza.data}</TableCell>
                    <TableCell>{deviza.furnizor}</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {deviza.materiale.length} materiale
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {deviza.materiale.map(m => m.nume).join(', ')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight="bold">
                        €{deviza.total.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={deviza.status}
                        color={deviza.status === 'aprobata' ? 'success' : deviza.status === 'in_asteptare' ? 'warning' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton color="primary">
                        <ViewIcon />
                      </IconButton>
                      <IconButton color="secondary">
                        <EditIcon />
                      </IconButton>
                      <IconButton color="default">
                        <DownloadIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Tab Bonuri Transfer */}
        <TabPanel value={activeTab} index={4}>
          <Typography variant="h6" gutterBottom>
            Bonuri Transfer
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Număr</TableCell>
                  <TableCell>Data</TableCell>
                  <TableCell>De la</TableCell>
                  <TableCell>La</TableCell>
                  <TableCell>Suma</TableCell>
                  <TableCell>Motiv</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Acțiuni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bonuriTransfer.map((bon) => (
                  <TableRow key={bon.id}>
                    <TableCell>{bon.numar}</TableCell>
                    <TableCell>{bon.data}</TableCell>
                    <TableCell>{bon.deLa}</TableCell>
                    <TableCell>{bon.la}</TableCell>
                    <TableCell>
                      <Typography fontWeight="bold">
                        €{bon.suma.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>{bon.motiv}</TableCell>
                    <TableCell>
                      <Chip
                        label={bon.status}
                        color={bon.status === 'efectuat' ? 'success' : bon.status === 'in_asteptare' ? 'warning' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton color="primary">
                        <ViewIcon />
                      </IconButton>
                      <IconButton color="secondary">
                        <EditIcon />
                      </IconButton>
                      <IconButton color="default">
                        <DownloadIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Tab Plăți */}
        <TabPanel value={activeTab} index={5}>
          <Typography variant="h6" gutterBottom>
            Plăți
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell>Tip</TableCell>
                  <TableCell>Document</TableCell>
                  <TableCell>Suma</TableCell>
                  <TableCell>Metodă plată</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Acțiuni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {plati.map((plata) => (
                  <TableRow key={plata.id}>
                    <TableCell>{plata.data}</TableCell>
                    <TableCell>
                      <Chip
                        label={plata.tip.replace('_', ' ')}
                        color="primary"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{plata.documentNumar}</TableCell>
                    <TableCell>
                      <Typography fontWeight="bold">
                        €{plata.suma.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={plata.metodaPlata}
                        color="info"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={plata.status}
                        color={plata.status === 'efectuata' ? 'success' : plata.status === 'in_asteptare' ? 'warning' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton color="primary">
                        <ViewIcon />
                      </IconButton>
                      <IconButton color="secondary">
                        <EditIcon />
                      </IconButton>
                      <IconButton color="default">
                        <DownloadIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Tab Gestiuni */}
        <TabPanel value={activeTab} index={6}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Gestiuni și Produse
            </Typography>
            <Tabs value={subTab} onChange={(e, newValue) => setSubTab(newValue)}>
              <Tab label="Gestiuni" />
              <Tab label="Produse în Stoc" />
              <Tab label="Mișcări Produse" />
              <Tab label="Introducere Produse" />
            </Tabs>
          </Box>

          {/* Sub-tab Gestiuni */}
          {activeTab === 6 && subTab === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Gestiuni
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {gestiuni.map((gestiune) => (
                  <Grid item xs={12} md={6} lg={4} key={gestiune.id}>
                    <Card>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                            <Inventory />
                          </Avatar>
                          <Box>
                            <Typography variant="h6">{gestiune.nume}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {gestiune.parohieNume}
                            </Typography>
                          </Box>
                        </Box>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Tip:</strong> {gestiune.tip}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Locație:</strong> {gestiune.locatie}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Responsabil:</strong> {gestiune.responsabil}
                        </Typography>
                        <Chip
                          label={gestiune.status}
                          color={gestiune.status === 'activ' ? 'success' : gestiune.status === 'inactiv' ? 'error' : 'warning'}
                          size="small"
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* Sub-tab Produse în Stoc */}
          {activeTab === 6 && subTab === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Produse în Stoc
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Produs</TableCell>
                      <TableCell>Categorie</TableCell>
                      <TableCell>Cantitate</TableCell>
                      <TableCell>Preț unitar</TableCell>
                      <TableCell>Valoare totală</TableCell>
                      <TableCell>Gestiune</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Data intrare</TableCell>
                      <TableCell>Acțiuni</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {produseGestiuni.map((produs) => (
                      <TableRow key={produs.id}>
                        <TableCell>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {produs.nume}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {produs.parohieNume}
                          </Typography>
                        </TableCell>
                        <TableCell>{produs.categorie}</TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {produs.cantitate} {produs.unitate}
                          </Typography>
                        </TableCell>
                        <TableCell>€{produs.pretUnitar.toLocaleString()}</TableCell>
                        <TableCell>
                          <Typography fontWeight="bold">
                            €{produs.valoareTotala.toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell>{produs.gestiuneNume}</TableCell>
                        <TableCell>
                          <Chip
                            label={produs.status.replace('_', ' ')}
                            color={
                              produs.status === 'in_stoc' ? 'success' :
                              produs.status === 'consumat' ? 'warning' :
                              produs.status === 'transferat' ? 'info' :
                              produs.status === 'vandut' ? 'primary' : 'error'
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{produs.dataIntrare}</TableCell>
                        <TableCell>
                          <IconButton color="primary">
                            <ViewIcon />
                          </IconButton>
                          <IconButton color="secondary">
                            <EditIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Sub-tab Mișcări Produse */}
          {activeTab === 6 && subTab === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Mișcări Produse
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Data</TableCell>
                      <TableCell>Tip</TableCell>
                      <TableCell>Produs</TableCell>
                      <TableCell>Cantitate</TableCell>
                      <TableCell>Gestiune</TableCell>
                      <TableCell>Document</TableCell>
                      <TableCell>Utilizator</TableCell>
                      <TableCell>Observații</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {miscariProduse.map((miscare) => (
                      <TableRow key={miscare.id}>
                        <TableCell>{miscare.data}</TableCell>
                        <TableCell>
                          <Chip
                            label={miscare.tip}
                            color={
                              miscare.tip === 'intrare' ? 'success' :
                              miscare.tip === 'iesire' ? 'error' :
                              miscare.tip === 'transfer' ? 'info' : 'warning'
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{miscare.produsNume}</TableCell>
                        <TableCell>{miscare.cantitate}</TableCell>
                        <TableCell>{miscare.gestiuneNume}</TableCell>
                        <TableCell>{miscare.documentNumar || '-'}</TableCell>
                        <TableCell>{miscare.utilizator}</TableCell>
                        <TableCell>{miscare.observatii || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Sub-tab Introducere Produse */}
          {activeTab === 6 && subTab === 3 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Introducere Produse din Documente
              </Typography>
              
              {/* Facturi Intrare */}
              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Facturi Intrare - Produse de introdus
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Factură</TableCell>
                        <TableCell>Furnizor</TableCell>
                        <TableCell>Descriere</TableCell>
                        <TableCell>Suma</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Acțiuni</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {facturiIntrare.map((factura) => (
                        <TableRow key={factura.id}>
                          <TableCell>{factura.numar}</TableCell>
                          <TableCell>{factura.furnizor}</TableCell>
                          <TableCell>{factura.descriere}</TableCell>
                          <TableCell>€{factura.total.toLocaleString()}</TableCell>
                          <TableCell>
                            <Chip
                              label={factura.status}
                              color={factura.status === 'platita' ? 'success' : 'warning'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={() => {/* Implementare introducere produse din factură */}}
                            >
                              Introduce Produse
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              {/* Devize Materiale */}
              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Devize Materiale - Produse de introdus
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Deviză</TableCell>
                        <TableCell>Furnizor</TableCell>
                        <TableCell>Materiale</TableCell>
                        <TableCell>Total</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Acțiuni</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {devizeMateriale.map((deviza) => (
                        <TableRow key={deviza.id}>
                          <TableCell>{deviza.numar}</TableCell>
                          <TableCell>{deviza.furnizor}</TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {deviza.materiale.length} materiale
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {deviza.materiale.map(m => `${m.nume} (${m.cantitate} ${m.unitate})`).join(', ')}
                            </Typography>
                          </TableCell>
                          <TableCell>€{deviza.total.toLocaleString()}</TableCell>
                          <TableCell>
                            <Chip
                              label={deviza.status}
                              color={deviza.status === 'aprobata' ? 'success' : 'warning'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={() => {/* Implementare introducere produse din deviză */}}
                            >
                              Introduce Produse
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              {/* Bonuri Transfer */}
              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Bonuri Transfer - Produse de introdus
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Bon</TableCell>
                        <TableCell>De la</TableCell>
                        <TableCell>La</TableCell>
                        <TableCell>Suma</TableCell>
                        <TableCell>Motiv</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Acțiuni</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bonuriTransfer.map((bon) => (
                        <TableRow key={bon.id}>
                          <TableCell>{bon.numar}</TableCell>
                          <TableCell>{bon.deLa}</TableCell>
                          <TableCell>{bon.la}</TableCell>
                          <TableCell>€{bon.suma.toLocaleString()}</TableCell>
                          <TableCell>{bon.motiv}</TableCell>
                          <TableCell>
                            <Chip
                              label={bon.status}
                              color={bon.status === 'efectuat' ? 'success' : 'warning'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={() => {/* Implementare introducere produse din bon transfer */}}
                            >
                              Introduce Produse
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Box>
          )}
        </TabPanel>

        {/* Tab Inventar */}
        <TabPanel value={activeTab} index={7}>
          {/* Filtre pentru inventar */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Caută în inventar..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Categorie</InputLabel>
                  <Select
                    value={filterCategorie}
                    onChange={(e) => setFilterCategorie(e.target.value)}
                    label="Categorie"
                  >
                    <MenuItem value="">Toate</MenuItem>
                    {inventoryCategories.map(category => (
                      <MenuItem key={category} value={category}>{category}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<FilterIcon />}
                  onClick={() => {
                    setSearchTerm('');
                    setFilterCategorie('');
                  }}
                >
                  Resetează
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Lista inventar */}
          <List>
            {filteredInventory.map((item) => (
              <React.Fragment key={item.id}>
                <ListItem>
                  <ListItemIcon>
                    <Inventory />
                  </ListItemIcon>
                  <ListItemText
                    primary={item.nume}
                    secondary={`${item.cantitate} ${item.unitate} • ${item.categorie}`}
                  />
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="h6" color="primary">
                      €{item.valoare.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Valoare totală
                    </Typography>
                  </Box>
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        </TabPanel>
      </Paper>

      {/* Dialog pentru vizualizare tranzacție */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Detalii tranzacție
        </DialogTitle>
        <DialogContent>
          {selectedTransaction && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Data
                </Typography>
                <Typography variant="body1">
                  {selectedTransaction.data}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Tip
                </Typography>
                <Chip
                  label={selectedTransaction.tip}
                  color={selectedTransaction.tip === 'venit' ? 'success' : 'error'}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Categorie
                </Typography>
                <Typography variant="body1">
                  {selectedTransaction.categorie}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Status
                </Typography>
                <Chip
                  label={selectedTransaction.status}
                  color={getStatusColor(selectedTransaction.status) as any}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  Descriere
                </Typography>
                <Typography variant="body1">
                  {selectedTransaction.descriere}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  Suma
                </Typography>
                <Typography variant="h5" color={selectedTransaction.tip === 'venit' ? 'success.main' : 'error.main'}>
                  {selectedTransaction.tip === 'venit' ? '+' : '-'}€{selectedTransaction.suma.toLocaleString()}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Închide</Button>
          <Button variant="contained" startIcon={<DownloadIcon />}>
            Descarcă
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Gestiune;
